import { Label } from "@/components/ui/label";
import { InputPassword } from "@altimateai/lego";
import { useFormik } from "formik";
import { FormAlert, FormSubmit } from "./components/form";
import { BaseContainer, Header } from "./components/base";
import { isFormFieldError, passwordSchema } from "./helper";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { decodeToken } from "react-jwt";
import { useMutation } from "@tanstack/react-query";
import { postResetPassword } from "../../helpers/apis";
import { useState } from "react";
import classNames from "classnames";
import styles from "./components/styles.module.scss";
import { validateFormPassword } from "./helper";
import { withZodSchema } from "formik-validator-zod";
import { PasswordValidation } from "./PasswordValidator";

const PASSWORD_INPUT_ID = "free-user-password-input";

interface DecodedToken {
  exp: number;
  nbf: number;
  [key: string]: unknown;
}

const Reset = () => {
  const navigate = useNavigate();
  const [validatePassword, setValidatePassword] = useState([0, 0, 0, 0, 0]);
  const { token } = useParams();
  const decodedToken = token ? decodeToken(token) : null;
  const { mutate, error } = useMutation({
    mutationFn: postResetPassword,
    onSuccess: data => {
      if (data?.ok) {
        navigate("/login");
      }
    },
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
    },
    validate: withZodSchema(passwordSchema),
    onSubmit: values => mutate({ ...values, token: token ?? "" }),
  });

  const typedToken = decodedToken as DecodedToken | null;

  if (typedToken && typedToken.exp < Date.now() / 1000) {
    const state = { inviteDate: typedToken.nbf };
    return <Navigate to="/expired" state={state} />;
  }

  if (!typedToken) {
    return <Navigate to="/expired" />;
  }

  const isFormDisabled = () => {
    if (validatePassword.includes(-1) || validatePassword.includes(0)) return true;

    return false;
  };

  const showFormFieldError = isFormFieldError(validation);
  const _error =
    (error instanceof Error ? error.message : undefined) || showFormFieldError("password");

  return (
    <BaseContainer>
      <Header>Set New Password</Header>
      <form
        onSubmit={e => {
          e.preventDefault();
          validation.handleSubmit();
        }}
        className="mt-3"
      >
        <div className="mb-3">
          <Label htmlFor={PASSWORD_INPUT_ID} className="form-label">
            New Password
          </Label>
          <div
            className={classNames(styles.tenant_form_password, {
              [styles.in_focus]:
                document.activeElement?.id === PASSWORD_INPUT_ID || validatePassword.includes(-1),
              [styles.is_invalid]: validatePassword.includes(-1),
            })}
          >
            <InputPassword
              id={PASSWORD_INPUT_ID}
              name="password"
              placeholder="Password"
              onBlur={() => {
                setValidatePassword(prev => prev.map(element => (element === 0 ? -1 : element)));
              }}
              onChange={e => {
                // Update Formik's state
                validation.handleChange(e);
                // Then validate the password
                validateFormPassword(e.target.value, setValidatePassword);
              }}
              disabled={validation.isSubmitting}
            />
          </div>
          {(document.activeElement?.id === PASSWORD_INPUT_ID || validatePassword.includes(-1)) && (
            <PasswordValidation validatePassword={validatePassword} />
          )}
        </div>

        <FormAlert error={_error} />

        <div className="mt-4">
          <FormSubmit disabled={isFormDisabled()}>Change Password</FormSubmit>
        </div>
      </form>
    </BaseContainer>
  );
};

export { Reset };
